import {
  Button,
  Stack,
  NumberInput,
  LoadingOverlay,
  Group,
  Table,
  TextInput,
  Select,
  Modal,
} from "@mantine/core"
import { DefaultModal } from "../../../../-components/default-modal"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useCallback, useRef } from "react"
import {
  useGetConditionsByResourceObjectId,
  useGetConditionsByResourceId,
  useCreateResourceObjects,
  useGetPaginatedResources,
  ResourceObjectDto,
  CreateResourceObjectsRequest,
} from "@/orval/gen"
import { PriceInput } from "./PriceInput"
import {
  showErrorNotification,
  showSuccessNotification,
} from "@/utils/form-submission-notifications"
import { ResourceTypeCategory } from "@/@client/Theasoft_DataClasses_Dispo"

interface Props {
  opened: boolean
  onClose(): void
  defaultResourceId?: number
  category?: ResourceTypeCategory
}

type Step = "initial" | "details"

export function CreateResourceObjectsModal({
  opened,
  onClose,
  defaultResourceId,
  category,
}: Props) {
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()
  const [resources, setResources] = useState<any[]>([])
  const [isLoadingResources, setIsLoadingResources] = useState(false)
  const { mutateAsync: createResourceObjectsAsync } = useCreateResourceObjects()
  const { mutateAsync: getPaginatedResourcesAsync } = useGetPaginatedResources()

  const [step, setStep] = useState<Step>("initial")
  const [createdObjects, setCreatedObjects] = useState<ResourceObjectDto[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [purchasePriceNum, setPurchasePriceNum] = useState<number | undefined>()
  const [resourceSearchTerm, setResourceSearchTerm] = useState("")

  const loadResources = useCallback(
    async (searchTerm = "") => {
      setIsLoadingResources(true)

      const result = await getPaginatedResourcesAsync({
        data: {
          category,
          availableCount: 1,
          name: searchTerm || undefined,
        },
      })
      setIsLoadingResources(false)
      setResources(result.data ?? [])
    },
    [getPaginatedResourcesAsync, category]
  )

  const form = useForm({
    initialValues: {
      resourceId: defaultResourceId?.toString() ?? (null as string | null),
      purchasePrice: "" as string,
      count: 1,
      conditionId: null as string | null,
    },
  })

  const selectedResourceId = form.values.resourceId
    ? Number(form.values.resourceId)
    : null

  const { data: conditions = [] } = useGetConditionsByResourceId(
    { resourceId: selectedResourceId || 0 },
    {
      query: {
        enabled: selectedResourceId !== null,
      },
    }
  )

  useEffect(() => {
    if (opened) {
      loadResources()
    }
  }, [opened, loadResources])

  React.useEffect(() => {
    if (opened) {
      setStep("initial")
      setCreatedObjects([])
      setPurchasePriceNum(undefined)
      form.setFieldValue("resourceId", defaultResourceId?.toString() ?? null)
    } else {
      form.reset()
    }
  }, [opened])

  const handleCreateObjects = async () => {
    if (!form.validate().hasErrors) {
      // Use the form value directly instead of searching in resources list
      // This avoids issues when resources are still loading or filtered
      const resourceId = form.values.resourceId
      if (!resourceId) {
        showErrorNotification(t("SelectResource") || "Resource not selected")
        return
      }

      const previewObjects: ResourceObjectDto[] = Array.from(
        { length: form.values.count },
        (_, i) =>
          ({
            resourceObjectId: -(i + 1),
            resourceId: Number(resourceId),
            name: "",
            identificationCode: "",
            resourceObjectConditionId: form.values.conditionId
              ? Number(form.values.conditionId)
              : null,
            purchasePrice: purchasePriceNum,
          }) as unknown as ResourceObjectDto
      )

      setCreatedObjects(previewObjects)
      setStep("details")
    }
  }

  const handleConfirmAndCreate = async () => {
    setIsCreating(true)
    try {
      const createRequest: CreateResourceObjectsRequest = {
        resourceId: Number(form.values.resourceId),
        objects: createdObjects.map((obj) => ({
          name: obj.name || undefined,
          identificationCode: obj.identificationCode || undefined,
          resourceObjectConditionId: obj.resourceObjectConditionId,
          purchasePrice: obj.purchasePrice,
        })),
      }

      await createResourceObjectsAsync({
        data: createRequest,
      })

      queryClient.invalidateQueries({
        queryKey: [
          "/api/ResourceObject/GetResourceObjectsByResourceId",
          { resourceId: Number(form.values.resourceId) },
        ],
      })
      queryClient.invalidateQueries({
        queryKey: ["/api/ResourceObject/GetPaginatedResourceObjects"],
      })

      showSuccessNotification(t("Created"))
      handleClose()
    } catch (e: unknown) {
      if (e instanceof Error) {
        showErrorNotification(e)
      } else {
        showErrorNotification(String(e))
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleUpdateObject = useCallback(
    (
      objectId: number,
      updates: {
        identificationCode?: string
        name?: string
        resourceObjectConditionId?: number | null
      }
    ) => {
      setCreatedObjects((prevObjects) =>
        prevObjects.map((obj) =>
          obj.resourceObjectId === objectId
            ? ({
                ...obj,
                name: updates.name ?? obj.name,
                identificationCode:
                  updates.identificationCode ?? obj.identificationCode,
                resourceObjectConditionId:
                  updates.resourceObjectConditionId !== undefined
                    ? updates.resourceObjectConditionId
                    : obj.resourceObjectConditionId,
              } as unknown as ResourceObjectDto)
            : obj
        )
      )
    },
    []
  )

  const handleClose = () => {
    if (step === "details" && createdObjects.length > 0) {
      queryClient.invalidateQueries({
        queryKey: ["getPaginatedResourceObjects"],
        exact: false,
      })
    }
    setStep("initial")
    setCreatedObjects([])
    onClose()
  }

  const handleBackToInitial = () => {
    setStep("initial")
  }

  return (
    <>
      <DefaultModal
        opened={opened && step === "initial"}
        onClose={handleClose}
        title={t("New")}
      >
        <LoadingOverlay visible={isCreating || isLoadingResources} />
        <form onSubmit={form.onSubmit(handleCreateObjects)}>
          <Stack>
            <Select
              label={t("Resource")}
              required
              placeholder={t("SelectResource")}
              data={(resources ?? []).map((r) => ({
                value: r.resourceId.toString(),
                label:
                  [r.name, r.resourceType].filter(Boolean).join(" / ") ?? "",
              }))}
              value={form.values.resourceId}
              onChange={(v) => form.setFieldValue("resourceId", v ?? null)}
              onSearchChange={(term) => {
                setResourceSearchTerm(term)
                loadResources(term)
              }}
              searchable
              error={form.errors.resourceId}
            />
            <Select
              label={t("Condition")}
              placeholder={t("SelectCondition")}
              data={(conditions ?? []).map((c) => ({
                value: c.resourceObjectConditionId.toString(),
                label: c.name ?? "",
              }))}
              value={form.values.conditionId}
              onChange={(v) => form.setFieldValue("conditionId", v ?? null)}
              searchable
              clearable
            />
            <PriceInput
              label={t("PurchasePrice")}
              required
              placeholder={t("PurchasePrice")}
              value={(form.values.purchasePrice ?? "").toString()}
              onChange={(cleanedInput, numValue) => {
                form.setFieldValue("purchasePrice", cleanedInput)
                setPurchasePriceNum(numValue)
              }}
              error={form.errors.purchasePrice}
            />
            <NumberInput
              label={t("Count")}
              required
              min={1}
              {...form.getInputProps("count")}
              error={form.errors.count}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={handleClose}>
                {t("Cancel")}
              </Button>
              <Button type="submit" loading={isCreating}>
                {t("Next")}
              </Button>
            </Group>
          </Stack>
        </form>
      </DefaultModal>

      <Modal
        opened={opened && step === "details"}
        onClose={handleClose}
        title={t("Details")}
        size="lg"
      >
        <LoadingOverlay visible={isCreating} />
        <Stack>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("SerialNumber")}</Table.Th>
                <Table.Th>{t("InventoryNumber")}</Table.Th>
                <Table.Th>{t("Condition")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {createdObjects.map((obj) => (
                <MemoizedResourceObjectDetailRow
                  key={obj.resourceObjectId}
                  object={obj}
                  conditions={conditions}
                  resources={resources}
                  onUpdate={(updates) =>
                    handleUpdateObject(obj.resourceObjectId, updates)
                  }
                />
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {t("Cancel")}
            </Button>
            <Button variant="default" onClick={handleBackToInitial}>
              {t("Back")}
            </Button>
            <Button onClick={handleConfirmAndCreate} loading={isCreating}>
              {t("Confirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

interface ResourceObjectDetailRowProps {
  object: ResourceObjectDto
  conditions: any[]
  resources: any[]
  onUpdate: (updates: {
    identificationCode?: string
    name?: string
    resourceObjectConditionId?: number | null
  }) => void
}

function ResourceObjectDetailRow({
  object,
  conditions,
  resources,
  onUpdate,
}: ResourceObjectDetailRowProps) {
  const { t } = useTranslation("app_server")
  const [isEditing, setIsEditing] = useState(false)
  const [identificationCode, setIdentificationCode] = useState(
    object.identificationCode ?? ""
  )
  const [name, setName] = useState(object.name ?? "")
  const [conditionId, setConditionId] = useState<string | null>(
    object.resourceObjectConditionId?.toString() ?? null
  )

  React.useEffect(() => {
    setIdentificationCode(object.identificationCode ?? "")
    setName(object.name ?? "")
    setConditionId(object.resourceObjectConditionId?.toString() ?? null)
  }, [object.identificationCode, object.name, object.resourceObjectConditionId])

  const { data: specificConditions = [] } = useGetConditionsByResourceObjectId(
    { resourceObjectId: object.resourceObjectId },
    {
      query: {
        enabled: object.resourceObjectId > 0,
      },
    }
  )

  const { data: resourceConditions = [] } = useGetConditionsByResourceId(
    { resourceId: object.resourceId },
    {
      query: {
        enabled: object.resourceObjectId < 0,
      },
    }
  )
  const applicableConditions =
    object.resourceObjectId > 0 ? specificConditions : resourceConditions

  const handleSave = useCallback(() => {
    onUpdate({
      identificationCode: identificationCode || undefined,
      name: name || undefined,
      resourceObjectConditionId: conditionId ? Number(conditionId) : null,
    })
  }, [name, identificationCode, conditionId, onUpdate])

  return (
    <Table.Tr>
      <Table.Td>
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleSave}
          placeholder={t("SerialNumber")}
          size="xs"
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          value={identificationCode}
          onChange={(e) => setIdentificationCode(e.target.value)}
          onBlur={handleSave}
          placeholder={t("InventoryNumber")}
          size="xs"
        />
      </Table.Td>
      <Table.Td>
        <Select
          value={conditionId}
          onChange={(v) => {
            setConditionId(v ?? null)
            onUpdate({
              identificationCode: identificationCode || undefined,
              name: name || undefined,
              resourceObjectConditionId: v ? Number(v) : null,
            })
          }}
          data={applicableConditions.map((c) => ({
            value: c.resourceObjectConditionId.toString(),
            label: c.name ?? "",
          }))}
          placeholder={t("SelectCondition")}
          size="xs"
          clearable
          searchable
        />
      </Table.Td>
    </Table.Tr>
  )
}

const MemoizedResourceObjectDetailRow = React.memo(
  ResourceObjectDetailRow,
  (prevProps, nextProps) => {
    return (
      prevProps.object.resourceObjectId === nextProps.object.resourceObjectId &&
      prevProps.object.name === nextProps.object.name &&
      prevProps.object.identificationCode ===
        nextProps.object.identificationCode &&
      prevProps.object.resourceObjectConditionId ===
        nextProps.object.resourceObjectConditionId &&
      prevProps.conditions === nextProps.conditions &&
      prevProps.resources === nextProps.resources &&
      prevProps.onUpdate === nextProps.onUpdate
    )
  }
)
